import {
  ApplicationRef,
  createComponent,
  DOCUMENT,
  EmbeddedViewRef,
  EnvironmentInjector,
  inject,
  Injectable,
} from '@angular/core';
import { NotificationComponent } from './notification.component';

export interface NotificationOptions {
  type: NotificationType;
  duration?: number;
}

export type NotificationType = 'success' | 'error' | 'warning';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private appRef = inject(ApplicationRef);
  private environmentInjector = inject(EnvironmentInjector);
  private document = inject(DOCUMENT);
  private notificationContainer: HTMLElement | null = null;

  show(
    message: string,
    options: NotificationOptions = { type: 'success', duration: 3000 },
  ) {
    // Ensure a single container exists for all notifications
    if (!this.notificationContainer) {
      this.notificationContainer = this.document.createElement('div');
      this.notificationContainer.classList.add('notification-container');
      this.notificationContainer.setAttribute('popover', 'manual');
      this.document.body.appendChild(this.notificationContainer);
      this.notificationContainer.showPopover();
    }

    // 1. Create the component reference
    const componentRef = createComponent(NotificationComponent, {
      environmentInjector: this.environmentInjector,
    });

    // 2. Set component inputs
    componentRef.setInput('message', message);
    componentRef.setInput('type', options.type);

    // 3. Attach view to Angular's change detection
    this.appRef.attachView(componentRef.hostView);

    // 4. Get the DOM element and append it to our container
    const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;
    this.notificationContainer.appendChild(domElem);

    const sub = componentRef.instance.ondestroy.subscribe(() => {
      componentRef.destroy();
      sub.unsubscribe();
    });

    setTimeout(() => componentRef.instance.destroy(), options.duration);
  }

  showSuccess(
    message: string,
    options: NotificationOptions = { type: 'success', duration: 3000 },
  ) {
    this.show(message, { ...options, type: 'success' });
  }

  showError(
    message: string,
    options: NotificationOptions = { type: 'error', duration: 3000 },
  ) {
    this.show(message, { ...options, type: 'error' });
  }

  showWarning(
    message: string,
    options: NotificationOptions = { type: 'warning', duration: 3000 },
  ) {
    this.show(message, { ...options, type: 'warning' });
  }
}
